import React, { useState, useEffect } from 'react';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const toUtcDate = (str) => {
  if (!str) return null;
  // SQLite datetime('now') returns UTC without 'Z' — force UTC parsing
  if (!str.includes('Z') && !str.includes('+')) return new Date(str + 'Z');
  return new Date(str);
};

const getElapsedText = (createdAt) => {
  if (!createdAt) return '';
  const now = new Date();
  const created = toUtcDate(createdAt);
  const diffMs = now - created;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return `${h}시간 ${m}분 전`;
};

const OrderCard = ({ order, onStatusChange }) => {
  const [elapsed, setElapsed] = useState(getElapsedText(order.createdAt || order.created_at));
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(getElapsedText(order.createdAt || order.created_at));
    }, 60000);
    return () => clearInterval(interval);
  }, [order.createdAt, order.created_at]);

  const status = order.status || 'pending';
  const items = order.items || order.orderItems || [];
  const tableNum = order.tableNumber || order.table_number || order.tableNum || '?';
  const totalAmount = order.totalAmount || order.total_amount || 0;
  const createdAt = order.createdAt || order.created_at;
  const isCancelled = status === 'cancelled';

  const isUrgent = (() => {
    if (status === 'cancelled' || !createdAt) return false;
    const diffMs = new Date() - toUtcDate(createdAt);
    return diffMs > 5 * 60 * 1000;
  })();

  const BADGE = {
    pending:   { label: '대기중',  cls: 'badge-pending' },
    accepted:  { label: '대기중',  cls: 'badge-pending' },
    served:    { label: '완료',    cls: 'badge-served' },
    cancelled: { label: '취소됨', cls: 'badge-cancelled' },
  };
  const badge = BADGE[status] || BADGE.pending;

  const BORDER = {
    pending: 'pending', accepted: 'pending',
    served: 'served', cancelled: 'cancelled',
  };

  const handleCancel = async () => {
    if (isUpdating) return;
    if (!window.confirm('정말 이 주문을 취소하시겠습니까?')) return;
    setIsUpdating(true);
    try {
      await onStatusChange(order._id || order.id, 'cancelled');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`order-card ${BORDER[status] || 'pending'} ${status === 'pending' ? 'new-order-flash' : ''}`}>
      {/* Header */}
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <div className="flex-center gap-12">
          <div className="order-table-badge">{tableNum}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>테이블 {tableNum}번</div>
            <div className={`elapsed-time ${isUrgent ? 'urgent' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              {elapsed}
              {isUrgent && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )}
            </div>
          </div>
        </div>
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
      </div>

      {/* Items */}
      <div className="order-items">
        {items.length === 0 ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>항목 없음</div>
        ) : (
          items.map((item, idx) => {
            const name = item.menuName || item.name || item.menu_name || '메뉴';
            const qty = item.quantity || item.qty || 1;
            const price = item.price || item.unitPrice || item.unit_price || 0;
            const subtotal = item.subtotal || price * qty;
            // options may be a JSON string or array
            let opts = item.options || [];
            if (typeof opts === 'string') {
              try { opts = JSON.parse(opts); } catch { opts = []; }
            }
            return (
              <div key={idx} className="order-item-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span className="order-item-name">{name} <span className="order-item-qty">x{qty}</span></span>
                  <span className="order-item-price">{formatWon(subtotal)}</span>
                </div>
                {opts.length > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', paddingLeft: 4 }}>
                    {opts.map((o) => `+ ${o.name}${o.price_delta > 0 ? ` (${formatWon(o.price_delta)})` : ''}`).join(' · ')}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Total */}
      <div className="order-total">
        <span>합계</span>
        <span style={{ color: 'var(--primary)' }}>{formatWon(totalAmount)}</span>
      </div>

      {/* Cancel button — only for non-cancelled orders */}
      {!isCancelled && (
        <button
          className="btn btn-danger w-full"
          style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onClick={handleCancel}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <span className="spinner spinner-sm" />
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              주문 취소
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default OrderCard;
